const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const toProductUrlPath = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  try {
    const parsed = /^https?:\/\//i.test(raw)
      ? new URL(raw)
      : new URL(raw, window.location.origin);
    return normalizeKey(parsed.pathname.replace(/\/+$/, ""));
  } catch {
    return normalizeKey(raw.replace(/\/+$/, ""));
  }
};

export const buildProductIdentity = (record = {}) => {
  const title = record?.title || record?.itemName || record?.name || "";
  const urlPath = toProductUrlPath(record?.url);
  const pagePath = normalizeKey(record?.supportingBehavior?.pagePath || "");
  return {
    itemId: normalizeKey(record?.itemId),
    shopifyProductId: normalizeKey(record?.shopifyProductId),
    urlPath,
    pagePath,
    title: normalizeKey(title),
  };
};

export const buildProductMatchKeys = (record = {}) => {
  const id = buildProductIdentity(record);
  return [
    id.itemId,
    id.shopifyProductId,
    id.urlPath,
    id.pagePath,
    id.title,
  ].filter(Boolean);
};

export const mergeProductRecords = (baseRecord = {}, overlayRecord = {}) => ({
  ...baseRecord,
  ...overlayRecord,
  waterfall: {
    ...(baseRecord?.waterfall || {}),
    ...(overlayRecord?.waterfall || {}),
  },
  conversionQuality: {
    ...(baseRecord?.conversionQuality || {}),
    ...(overlayRecord?.conversionQuality || {}),
  },
  commercialOutcome: {
    ...(baseRecord?.commercialOutcome || {}),
    ...(overlayRecord?.commercialOutcome || {}),
  },
  supportingBehavior: {
    ...(baseRecord?.supportingBehavior || {}),
    ...(overlayRecord?.supportingBehavior || {}),
  },
  competitorPricing:
    overlayRecord?.competitorPricing ?? baseRecord?.competitorPricing ?? null,
  trends: overlayRecord?.trends ?? baseRecord?.trends ?? null,
});

export const attachNarrativeToProduct = (record = {}, narrative = null) => {
  if (!narrative) return record;
  return {
    ...record,
    interpretation: {
      ...(record?.interpretation || {}),
      overall: narrative.headline || record?.interpretation?.overall || null,
      standout:
        narrative.conversionComment || record?.interpretation?.standout || null,
      meaning:
        narrative.commercialComment || record?.interpretation?.meaning || null,
      nextStep:
        narrative.recommendation || record?.interpretation?.nextStep || null,
    },
  };
};

export const createProductLookup = (records = []) => {
  const lookup = new Map();
  (Array.isArray(records) ? records : []).forEach((record) => {
    buildProductMatchKeys(record).forEach((key) => {
      if (!lookup.has(key)) {
        lookup.set(key, record);
      }
    });
  });
  return lookup;
};

export const computeProductTrends = (current = {}, previous = null) => {
  if (!previous) return null;

  const currentViews = Number(
    current?.waterfall?.views ??
      current?.waterfall?.pdpViews ??
      current?.itemViewEvents ??
      0,
  ) || 0;
  const previousViews = Number(
    previous?.waterfall?.views ??
      previous?.waterfall?.pdpViews ??
      previous?.itemViewEvents ??
      0,
  ) || 0;

  const currentAtcRate = Number(
    current?.conversionQuality?.atcRate ??
      current?.conversionQuality?.pdpToAtcRate ??
      current?.cartToViewRate ??
      0,
  ) || 0;
  const previousAtcRate = Number(
    previous?.conversionQuality?.atcRate ??
      previous?.conversionQuality?.pdpToAtcRate ??
      previous?.cartToViewRate ??
      0,
  ) || 0;

  const currentPurchaseRate = Number(
    current?.conversionQuality?.purchaseToViewRate ??
      current?.conversionQuality?.pdpToPurchaseRate ??
      current?.purchaseToViewRate ??
      0,
  ) || 0;
  const previousPurchaseRate = Number(
    previous?.conversionQuality?.purchaseToViewRate ??
      previous?.conversionQuality?.pdpToPurchaseRate ??
      previous?.purchaseToViewRate ??
      0,
  ) || 0;

  const currentRevenue = Number(
    current?.commercialOutcome?.revenue ??
      current?.itemRevenue ??
      0,
  ) || 0;
  const previousRevenue = Number(
    previous?.commercialOutcome?.revenue ??
      previous?.itemRevenue ??
      0,
  ) || 0;

  const trafficChangePct =
    previousViews > 0 ? ((currentViews - previousViews) / previousViews) * 100 : null;
  const revenueChangePct =
    previousRevenue > 0 ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : null;

  return {
    trafficChangePct:
      trafficChangePct == null ? null : Number(trafficChangePct.toFixed(1)),
    revenueChangePct:
      revenueChangePct == null ? null : Number(revenueChangePct.toFixed(1)),
    atcRateChangePp: Number(((currentAtcRate - previousAtcRate) * 100).toFixed(1)),
    purchaseRateChangePp: Number(
      ((currentPurchaseRate - previousPurchaseRate) * 100).toFixed(1),
    ),
    searchTrendChangePct: null,
  };
};

export const attachComparisonTrends = ({
  record,
  comparisonLookup,
}) => {
  if (!record) return record;
  const comparisonMatch = buildProductMatchKeys(record)
    .map((key) => comparisonLookup.get(key))
    .find(Boolean);

  if (!comparisonMatch) return record;

  return {
    ...record,
    trends: computeProductTrends(record, comparisonMatch),
  };
};

/**
 * Resolve the active window payload from a rollup report's `data.windows`.
 * Falls back to the entire `data` object for legacy single-window reports.
 */
export const resolveWindowPayload = (data = {}, activeWindowKey = null) => {
  const allWindows = Array.isArray(data?.windows) ? data.windows : [];
  if (!allWindows.length) return data || {};
  if (activeWindowKey) {
    const selected = allWindows.find((w) => String(w?.key) === String(activeWindowKey));
    if (selected) return selected;
    console.warn("[ConversionAndPdpViewer] selected window payload not found", {
      activeWindowKey,
      availableWindowKeys: allWindows.map((w) => w?.key || null),
      generationKey: data?.generation_key || null,
      reportingPeriod: data?.reporting_period || null,
    });
    return {};
  }
  const resolved =
    allWindows.find((w) => String(w?.key) === String(data?.default_window_key)) ||
    allWindows[0];
  return resolved || {};
};

export const resolveProductRecord = ({
  seedRecord,
  deepDiveLookup,
  narrativeLookup,
}) => {
  if (!seedRecord) return null;
  const keys = buildProductMatchKeys(seedRecord);
  const deepDiveMatch = keys
    .map((key) => deepDiveLookup.get(key))
    .find(Boolean);
  const narrativeMatch = keys
    .map((key) => narrativeLookup.get(key))
    .find(Boolean);

  const merged = deepDiveMatch
    ? mergeProductRecords(seedRecord, deepDiveMatch)
    : seedRecord;

  return attachNarrativeToProduct(merged, narrativeMatch);
};
